import dayjs from 'dayjs'
import { GeoPoint } from './GeoPoint'
import { Media } from './Media'
import { UserGenders } from './UserProfile'
import { distanceGet } from 'geolib'
import { IGeoPoint } from './GeoPoint'

export const GroupApplication = [ 'open', 'request', 'invite' ] as const
export const GroupVisibility = [ 'public', 'private' ] as const
export const GroupStatus = [ 'pending', 'published' ] as const
export const GroupTypes = [ 'group', 'topic' ] as const

export const GroupRoles = [ 'admin', 'organizer' ] as const
export const GroupFeatures = [ 'posts', 'events', 'members', 'conversations', 'join' ] as const

export const GroupAges = [
  { value: '25_or_less', min: 18, max: 25 },
  { value: '20_to_35', min: 20, max: 35 },
  { value: '30_to_45', min: 30, max: 45 },
  { value: '40_to_65', min: 40, max: 65 },
  { value: '60_and_up', min: 60, max: 99 },
] as const

export const GroupAgesValues = GroupAges.map(g => g.value) 

export interface IGroup {
  _id: string

  name: string
  emoji: string
  slug: string
  status: typeof GroupStatus[number]
  
  application: typeof GroupApplication[number]
  visibility: typeof GroupVisibility[number]
  
  type: typeof GroupTypes[number]

  city: string
  location: string
  icon: string
  geolocation: IGeoPoint
  geolocationRadius: number

  categories: string[]

  language: string

  // DETAILS
  description: string
  banner: Media

  minAge: number
  maxAge: number
  ages: typeof GroupAgesValues
  genders: (typeof UserGenders[number])[]
  mixity: string

  isOfficial: boolean
  isSpicy: boolean

  invites?: string[]

  features: typeof GroupFeatures[number][]

  // EVENTS
  events: string[]
  flairs: {
    _id: string
    label: string
    color: string
  }[]

  // MEMBERS
  adminRoles: {
    _id: string
    role: typeof GroupRoles[number]
  }[]

  members: string[]
  requested: {
    _id: string
    intro: string
  }[]
  invited: string[]

  conversations: string[]

  // VIRTUALS
  membersCount: number
  owner: string
  lastActiveAt: Date
  createdAt: Date
}

export interface Group extends IGroup {}

export class Group implements IGroup {
  coordinates: [number, number]
  geolocation: GeoPoint
  
  constructor(props: IGroup) {

    Object.assign(this, {
      ...props,
      banner: props.banner ? new Media(props.banner) : null,
      geolocation: new GeoPoint(props.geolocation),
      coordinates: new GeoPoint(props.geolocation)?.coordinates,
      lastActiveAt: props.lastActiveAt ? new Date(props.lastActiveAt) : new Date(props.createdAt)
    })
  }

  public get ageLabel () {
    if (this.minAge === 99) return null
    if (this.minAge === 18 && this.maxAge === 99) return { key: 'components.Group.ages.all' }
    if (this.maxAge === 99) return { key: 'components.Group.ages.min', params: { min: this.minAge} }
 
    return { key: 'components.Group.ages.minMax', params: { min: this.minAge, max: this.maxAge } }
  }

  public isAdmin (_id = '') {
    return this.adminRoles?.find(a => a._id === _id && a.role === 'admin') ? true : false
  }

  public isOrga (_id = '') {
    return this.adminRoles?.find(a => a._id === _id && a.role === 'organizer') ? true : false
  }

  public distance (coordinates?: [number, number] | null) {
    const distanceKM = distanceGet(coordinates ?? [0, 0], this.coordinates ?? [0, 0], 1000)

    return distanceKM / 1000
  }

  public flairify (flairs: string[]) {
    return (flairs ?? []).map(flair => {
      return (this.flairs ?? []).find(t => t._id === flair)
    }).filter(t => t) as { _id: string, label: string, color: string }[]
  }

  public isLocal (coordinates?: [number, number] | null) {
    return this.geolocationRadius <= 40 && this.distance(coordinates) < this.geolocationRadius
  }

  public hasAdminRights (_id = '') {
    return this.isAdmin(_id)
  }

  public hasOrgaRights (_id = '') {
    return this.isAdmin(_id) || this.isOrga(_id)
  }

  public isMember (_id = '') {
    return (this.members ?? []).find(a => a == _id) || this.isAdmin(_id) ? true : false
  }

  public isPending (_id = '') {
    return !this.isMember(_id) && (this.requested ?? []).find(a => a._id == _id) ? true : false
  }

  public getRole(_id = '') {
    if (this.isAdmin(_id)) return `admin`
    if (this.isOrga(_id)) return `organizer`

    return ''
  }
}